import { confirmar } from '@/components/ui/confirm-dialog';
import { useEffect, useState } from 'react';
import { margenParaLista } from '@/features/pricer-datec/utils/ficha-de-observador';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeGestion } from '@/features/cotiza/components/encabezado-de-gestion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import {
  Plus,
  Eye,
  Edit,
  Trash2,
  Copy,
  FileText,
  Tags,
  BookmarkPlus,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  GitBranch,
  Search,
  X,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIntegrationStore } from '@/features/integrations/store/integration-store';
import { useQuoteStore } from '../store/quote-store';
import { formatCurrency, formatDateString } from '@/lib/utils/format';
import { QuoteStatus } from '@/types';
import { FadeIn } from '@/components/motion';
import { ChipDeEstado } from '../components/chip-de-estado';
import { etiquetaDeEstado } from '@/features/pricer-datec/components/estado-cotizacion';
import { TagBadge } from '../components/tag-badge';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { destinoNuevaCotizacion } from '../utils/destino-nueva-cotizacion';
import { TagSelector } from '../components/tag-selector';
import { MargenDeLaCotizacion } from '../components/margen-de-la-cotizacion';
import { pricerDatecService } from '@/features/pricer-datec/services/pricer-datec.service';
import type { PricerDatecQuoteSummary } from '@/features/pricer-datec/types/pricer-datec.types';

export function QuotesListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const usuario = useAuthStore((estado) => estado.user);
  // Antes iba siempre al Pricer Datec: a un cliente sin ese módulo lo dejaba en
  // «Módulo no disponible». Misma regla que el botón del Dashboard.
  const { canSeeMenu } = useAuth();
  const nuevaCotizacion = destinoNuevaCotizacion(canSeeMenu);
  const {
    quotes,
    templates,
    tags,
    isLoading,
    fetchQuotes,
    fetchTemplates,
    fetchTags,
    deleteQuote,
    createFromTemplate,
    saveAsTemplate,
  } = useQuoteStore();

  const {
    configs,
    triggerSync,
    fetchConfigs: fetchIntegrationConfigs,
    syncing,
  } = useIntegrationStore();
  const c4cConfig = configs.find((c) => c.type === 'SAP_C4C' && c.isActive);

  const [activeTab, setActiveTab] = useState('quotes');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tagSelectorQuoteId, setTagSelectorQuoteId] = useState<string | null>(null);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [templateQuoteId, setTemplateQuoteId] = useState<string | null>(null);
  // Cotizaciones PD en DRAFT: muestran como rows propias mientras no tengan Quote linked.
  // Las submitted ya aparecen en `quotes` (con un Quote linked en el backend), pero su
  // click debe redirigir al editor PD — para eso mantenemos el map code → pdId.
  const [pricerDatecQuotes, setPricerDatecQuotes] = useState<PricerDatecQuoteSummary[]>([]);

  useEffect(() => {
    fetchQuotes();
    fetchTags();
    fetchIntegrationConfigs();
    // Solo necesitamos los DRAFT del Pricer Datec (aún sin Quote linked).
    // Los demás vienen en /api/quotes con `pricerDatecId` anotado por el backend,
    // así evitamos una segunda call que traía toda la lista.
    pricerDatecService
      .listQuotes({ status: 'DRAFT' })
      .then(setPricerDatecQuotes)
      .catch(() => setPricerDatecQuotes([]));
  }, []);

  const handleC4cSync = async (direction: 'inbound' | 'outbound') => {
    if (!c4cConfig) return;
    try {
      const result = await triggerSync(c4cConfig.id, direction);
      toast({
        title: 'Sincronización C4C completada',
        description: `${result.successCount} exitosos, ${result.failedCount} fallidos`,
        variant: 'success',
      });
      fetchQuotes();
    } catch (error: any) {
      toast({
        title: 'Error al sincronizar con C4C',
        description: error?.response?.data?.error || error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'templates' && templates.length === 0) {
      fetchTemplates();
    }
  }, [activeTab]);

  const handleCreate = () => {
    if (nuevaCotizacion) navigate(nuevaCotizacion.href);
  };

  const handleView = (id: string, pricerDatecId?: string | null) => {
    // Si el quote viene del Pricer Datec, el backend nos manda su pricerDatecId
    // directo — sin necesidad de mantener un map separado.
    if (pricerDatecId) {
      navigate(`/pricer-datec/${pricerDatecId}`);
      return;
    }
    navigate(`/quotes/${id}`);
  };

  // Map para mapear code (PD-2026-XXXX) → id real del Pricer Datec, usado
  // por handleEdit cuando el quote estándar referenia un PD pero llegamos
  // sin el id explícito (solo el code).
  const pricerDatecCodeToId = pricerDatecQuotes.reduce<Record<string, string>>((acc, pdq) => {
    acc[pdq.code] = pdq.id;
    return acc;
  }, {});

  const handleEdit = (id: string, code?: string) => {
    if (code && pricerDatecCodeToId[code]) {
      navigate(`/pricer-datec/${pricerDatecCodeToId[code]}`);
      return;
    }
    navigate(`/quotes/${id}/edit`);
  };

  const handleDelete = async (id: string) => {
    if (
      !(await confirmar({
        title: '¿Eliminar esta cotización?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await deleteQuote(id);
      toast({ title: 'Cotización eliminada', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    try {
      const quote = await createFromTemplate(templateId);
      toast({ title: 'Cotización creada desde plantilla', variant: 'success' });
      navigate(`/quotes/${quote.id}`);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSaveAsTemplate = async (quoteId: string) => {
    if (!templateNameInput.trim()) return;
    try {
      await saveAsTemplate(quoteId, templateNameInput.trim());
      toast({ title: 'Plantilla guardada', variant: 'success' });
      setTemplateQuoteId(null);
      setTemplateNameInput('');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getStatusBadge = (status: string) => <ChipDeEstado status={status} />;

  const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const filteredQuotes = (() => {
    let list = quotes;
    if (filterTag) list = list.filter((q) => q.tags?.some((t) => t.id === filterTag));
    const q = normalize(search.trim());
    if (q) {
      list = list.filter((row) => {
        const cb = (row as any).createdBy;
        const haystack = normalize(
          [
            row.code,
            row.clientName,
            (row as any).model?.name,
            (row as any).model?.code,
            row.status,
            etiquetaDeEstado(row.status, false).texto,
            row.finalPrice?.toString(),
            cb ? `${cb.firstName} ${cb.lastName} ${cb.email}` : '',
          ]
            .filter(Boolean)
            .join(' ')
        );
        return haystack.includes(q);
      });
    }
    return list;
  })();

  const filteredPricerDatecQuotes = (() => {
    const q = normalize(search.trim());
    if (!q) return pricerDatecQuotes;
    return pricerDatecQuotes.filter((row) => {
      const cb = (row as any).createdBy;
      return normalize(
        [
          row.code,
          (row as any).clientName,
          (row as any).status,
          etiquetaDeEstado((row as any).status, false).texto,
          cb ? `${cb.firstName} ${cb.lastName} ${cb.email}` : '',
        ]
          .filter(Boolean)
          .join(' ')
      ).includes(q);
    });
  })();

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Gestión (artefacto, 19-oct): el paso «Cotizaciones». */}
          <EncabezadoDeGestion
            activa="cotizaciones"
            titulo="Cotizaciones"
            subtitulo="Tus cotizaciones y propuestas comerciales."
            acciones={
              <>
                {c4cConfig && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" disabled={syncing === c4cConfig.id}>
                        <RefreshCw
                          className={`h-4 w-4 ${syncing === c4cConfig.id ? 'animate-spin' : ''}`}
                        />
                        Sincronizar C4C
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleC4cSync('inbound')}>
                        <ArrowDownToLine className="h-4 w-4 mr-2" />
                        Importar Oportunidades
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleC4cSync('outbound')}>
                        <ArrowUpFromLine className="h-4 w-4 mr-2" />
                        Exportar Cotizaciones
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {nuevaCotizacion && (
                  <Button
                    variant="degradado"
                    size="lg"
                    className="px-5"
                    data-tour="quotes-new-btn"
                    onClick={handleCreate}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    {nuevaCotizacion.label}
                  </Button>
                )}
              </>
            }
          />

          {/* Tabs */}
          <FadeIn delay={0.15}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList data-tour="quotes-tabs">
                <TabsTrigger value="quotes">
                  <FileText className="w-4 h-4 mr-1.5" />
                  Cotizaciones ({quotes.length + pricerDatecQuotes.length})
                </TabsTrigger>
                <TabsTrigger value="templates">
                  <Copy className="w-4 h-4 mr-1.5" />
                  Plantillas ({templates.length})
                </TabsTrigger>
              </TabsList>

              {/* Quotes Tab */}
              <TabsContent value="quotes">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <CardTitle>Cotizaciones</CardTitle>
                        <CardDescription>Las tuyas y las que te compartieron</CardDescription>
                      </div>
                      <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                        <Input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Buscar por código, cliente, modelo, estado..."
                          className="pl-9 pr-9"
                        />
                        {search && (
                          <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                            aria-label="Limpiar búsqueda"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {/* Tag filter */}
                      {tags.length > 0 && (
                        <div data-tour="quotes-filters" className="flex items-center gap-2">
                          <Tags className="w-4 h-4 text-muted-foreground" />
                          <div className="flex gap-1 flex-wrap">
                            <button
                              onClick={() => setFilterTag(null)}
                              className={`text-xs px-2 py-1 rounded-full border ${
                                !filterTag
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-accent'
                              }`}
                            >
                              Todas
                            </button>
                            {tags.map((tag) => (
                              <button
                                key={tag.id}
                                onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  filterTag === tag.id ? 'ring-2 ring-primary' : ''
                                }`}
                                style={{
                                  backgroundColor:
                                    filterTag === tag.id ? `${tag.color}30` : undefined,
                                  borderColor: tag.color,
                                  color: tag.color,
                                }}
                              >
                                {tag.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="space-y-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-4">
                            <Skeleton variant="shimmer" className="h-5 w-24" />
                            <Skeleton variant="shimmer" className="h-5 w-40" />
                            <Skeleton variant="shimmer" className="h-5 w-32" />
                            <Skeleton variant="shimmer" className="h-5 w-20 ml-auto" />
                          </div>
                        ))}
                      </div>
                    ) : filteredQuotes.length === 0 && filteredPricerDatecQuotes.length === 0 ? (
                      <div className="text-center py-8">
                        {/*
                          El dia del cambio de visibilidad mucha gente abre esta
                          pantalla y ve cero. Tiene que decir que paso y que hacer,
                          no quedarse en blanco.
                        */}
                        <p className="text-muted-foreground">
                          {search
                            ? `Sin resultados para "${search}"`
                            : filterTag
                              ? 'No hay cotizaciones con esta etiqueta'
                              : 'Todavía no creaste ninguna cotización, y nadie te compartió una.'}
                        </p>
                        {!filterTag && !search && (
                          <p className="mt-2 text-sm text-muted-foreground">
                            Cada cotización la ve quien la creó. Si necesitás ver la de un
                            compañero, pedile que te la comparta desde el bloque «Acceso» de la
                            ficha.
                          </p>
                        )}
                        {!filterTag && !search && nuevaCotizacion && (
                          <Button onClick={handleCreate} variant="outline" className="mt-4">
                            <Plus className="w-4 h-4 mr-2" />
                            Crear Primera Cotización
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div data-tour="quotes-table" className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            {/*
                              19-oct (artefacto): de 12 columnas a 9. En 1440 px el código
                              se partía en tres líneas y la fecha quedaba cortada. El nombre,
                              las etiquetas y el origen (clonada de…) van debajo del código.
                            */}
                            <TableRow>
                              <TableHead>Cotización</TableHead>
                              <TableHead>Cliente</TableHead>
                              <TableHead className="hidden 2xl:table-cell">Modelo</TableHead>
                              <TableHead className="hidden 2xl:table-cell">Creado por</TableHead>
                              <TableHead className="text-right whitespace-nowrap">
                                Precio final
                              </TableHead>
                              <TableHead className="text-right">Margen</TableHead>
                              <TableHead data-tour="quotes-status">Estado</TableHead>
                              <TableHead>Fecha</TableHead>
                              <TableHead data-tour="quotes-actions" className="text-right">
                                Acciones
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredPricerDatecQuotes.map((pdq) => (
                              <TableRow
                                key={`pd-${pdq.id}`}
                                className="cursor-pointer hover:bg-accent animate-fade-in"
                                onClick={() => navigate(`/pricer-datec/${pdq.id}`)}
                              >
                                <TableCell className="max-w-[240px]">
                                  <p className="whitespace-nowrap font-mono text-sm font-semibold">
                                    {pdq.code}
                                  </p>
                                  {(pdq as any).name && (
                                    <p
                                      className="truncate text-xs text-muted-foreground"
                                      title={(pdq as any).name}
                                    >
                                      {(pdq as any).name}
                                    </p>
                                  )}
                                  <Badge variant="info" className="mt-1">
                                    Pricer Datec
                                  </Badge>
                                </TableCell>
                                <TableCell className="max-w-[240px]">
                                  <p className="truncate font-medium" title={pdq.clientName}>
                                    {pdq.clientName}
                                  </p>
                                  <p className="text-xs text-muted-foreground">{pdq.tipoQuote}</p>
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell text-xs text-muted-foreground">
                                  —
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell whitespace-nowrap text-sm">
                                  {pdq.createdBy
                                    ? `${pdq.createdBy.firstName} ${pdq.createdBy.lastName}`
                                    : '—'}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums">
                                  {formatCurrency(pdq.totalConBoleta, 'USD')}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {margenParaLista(pdq.margenGlobal)}
                                  {typeof pdq.margenGlobal === 'number' && (
                                    <p className="text-[11px] font-sans text-muted-foreground whitespace-nowrap">
                                      Margen Bruto
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <ChipDeEstado status={pdq.status} />
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                  {formatDateString(pdq.fecha)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/pricer-datec/${pdq.id}`);
                                    }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredQuotes.map((quote, index) => (
                              <TableRow
                                key={quote.id}
                                className="cursor-pointer hover:bg-accent animate-fade-in"
                                style={{
                                  animationDelay: `${index * 50}ms`,
                                  animationFillMode: 'backwards',
                                }}
                                onClick={() => handleView(quote.id, (quote as any).pricerDatecId)}
                              >
                                <TableCell className="max-w-[260px]">
                                  <p className="whitespace-nowrap font-mono text-sm font-semibold">
                                    {quote.code}
                                  </p>
                                  {(quote as any).name && (
                                    <p
                                      className="truncate text-xs text-muted-foreground"
                                      title={(quote as any).name}
                                    >
                                      {(quote as any).name}
                                    </p>
                                  )}
                                  {/* Por qué está acá una que no es tuya. */}
                                  {(quote as any).createdById &&
                                    (quote as any).createdById !== usuario?.id && (
                                      <p className="text-xs text-muted-foreground">
                                        Compartida
                                        {(quote as any).createdBy
                                          ? ` por ${(quote as any).createdBy.firstName}`
                                          : ''}
                                      </p>
                                    )}
                                  {quote.clonedFrom || quote.sourceQuote ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const originId =
                                              quote.clonedFrom?.id || quote.sourceQuote?.id;
                                            if (originId) navigate(`/quotes/${originId}`);
                                          }}
                                        >
                                          <GitBranch className="w-3.5 h-3.5" />
                                          <span className="font-mono">
                                            {quote.clonedFrom?.code || quote.sourceQuote?.code}
                                          </span>
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {quote.clonedFrom
                                          ? `Clonada de ${quote.clonedFrom.code} (${quote.clonedFrom.clientName})`
                                          : `Costos de ${quote.sourceQuote!.code} (${quote.sourceQuote!.clientName})`}
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : null}
                                  <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {quote.tags?.map((tag) => (
                                        <TagBadge key={tag.id} tag={tag} />
                                      ))}
                                      <Popover
                                        open={tagSelectorQuoteId === quote.id}
                                        onOpenChange={(open) =>
                                          setTagSelectorQuoteId(open ? quote.id : null)
                                        }
                                      >
                                        <PopoverTrigger asChild>
                                          <button className="w-5 h-5 rounded-full border border-dashed border-muted-foreground/40 flex items-center justify-center hover:bg-accent">
                                            <Plus className="w-3 h-3 text-muted-foreground" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="p-0 w-auto" align="start">
                                          <TagSelector
                                            quoteId={quote.id}
                                            selectedTags={quote.tags || []}
                                            onClose={() => setTagSelectorQuoteId(null)}
                                          />
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="max-w-[240px]">
                                  <p className="truncate font-medium" title={quote.clientName}>
                                    {quote.clientName}
                                  </p>
                                  <p className="truncate text-xs text-muted-foreground">
                                    {quote.clientEmail}
                                  </p>
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell max-w-[180px]">
                                  <span className="line-clamp-2 text-sm">{quote.model?.name}</span>
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell whitespace-nowrap text-sm">
                                  {(quote as any).createdBy
                                    ? `${(quote as any).createdBy.firstName} ${(quote as any).createdBy.lastName}`
                                    : '—'}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums">
                                  {formatCurrency(quote.finalPrice, quote.currency?.code ?? 'USD')}
                                </TableCell>
                                <TableCell className="text-right">
                                  {/* PD guarda margen operativo en actualMargin; cotizaciones estándar, margen bruto.
                                      FP-4 (M-1): sin «Ver costos y márgenes» el espejo llega sin margen: «—». */}
                                  <MargenDeLaCotizacion
                                    actualMargin={quote.actualMargin}
                                    targetMargin={quote.targetMargin}
                                    floorMargin={quote.model?.floorMarginPercentage}
                                    costosOcultos={quote.costosOcultos}
                                    etiqueta={
                                      pricerDatecCodeToId[quote.code] ? 'Markup' : 'Margen Bruto'
                                    }
                                  />
                                </TableCell>
                                <TableCell>{getStatusBadge(quote.status)}</TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                  {formatDateString(quote.createdAt)}
                                </TableCell>
                                <TableCell
                                  className="text-right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex justify-end gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() =>
                                            handleView(quote.id, (quote as any).pricerDatecId)
                                          }
                                        >
                                          <Eye className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Ver detalle</TooltipContent>
                                    </Tooltip>
                                    {(quote.status === QuoteStatus.DRAFT ||
                                      quote.status === QuoteStatus.ADJUSTMENT_REQUESTED) && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleEdit(quote.id, quote.code)}
                                          >
                                            <Edit className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Editar</TooltipContent>
                                      </Tooltip>
                                    )}
                                    {/* Save as template (FP-4: el espejo de un Pricer no se guarda como plantilla) */}
                                    {!pricerDatecCodeToId[quote.code] && (
                                      <Popover
                                        open={templateQuoteId === quote.id}
                                        onOpenChange={(open) => {
                                          setTemplateQuoteId(open ? quote.id : null);
                                          if (!open) setTemplateNameInput('');
                                        }}
                                      >
                                        <PopoverTrigger asChild>
                                          <Button size="sm" variant="ghost">
                                            <BookmarkPlus className="w-4 h-4" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-64" align="end">
                                          <div className="space-y-2">
                                            <p className="text-sm font-medium">
                                              Guardar como Plantilla
                                            </p>
                                            <Input
                                              placeholder="Nombre de la plantilla..."
                                              value={templateNameInput}
                                              onChange={(e) => setTemplateNameInput(e.target.value)}
                                              className="h-8 text-sm"
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter')
                                                  handleSaveAsTemplate(quote.id);
                                              }}
                                            />
                                            <Button
                                              size="sm"
                                              className="w-full"
                                              onClick={() => handleSaveAsTemplate(quote.id)}
                                              disabled={!templateNameInput.trim()}
                                            >
                                              Guardar
                                            </Button>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                    {quote.status === QuoteStatus.DRAFT && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDelete(quote.id)}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Eliminar</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="templates">
                <Card>
                  <CardHeader>
                    <CardTitle>Plantillas</CardTitle>
                    <CardDescription>
                      Plantillas guardadas para crear cotizaciones rápidamente
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={i} className="flex items-center gap-4">
                            <Skeleton variant="shimmer" className="h-5 w-24" />
                            <Skeleton variant="shimmer" className="h-5 w-40" />
                            <Skeleton variant="shimmer" className="h-5 w-20 ml-auto" />
                          </div>
                        ))}
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">
                          No hay plantillas. Guarda una cotización como plantilla para empezar.
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nombre</TableHead>
                              <TableHead>Cliente Base</TableHead>
                              <TableHead>Modelo</TableHead>
                              <TableHead className="text-right">Precio</TableHead>
                              <TableHead>Creada</TableHead>
                              <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {templates.map((tpl) => (
                              <TableRow key={tpl.id}>
                                <TableCell className="font-semibold">
                                  {tpl.templateName || tpl.code}
                                </TableCell>
                                <TableCell>{tpl.clientName}</TableCell>
                                <TableCell>{tpl.model?.name || '-'}</TableCell>
                                <TableCell className="text-right font-semibold">
                                  {tpl.currency?.symbol}{' '}
                                  {formatCurrency(tpl.finalPrice).replace(/[^\d,.-]/g, '')}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDateString(tpl.createdAt)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    onClick={() => handleCreateFromTemplate(tpl.id)}
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Usar Plantilla
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </FadeIn>
        </div>
      </div>
    </AppLayout>
  );
}

export default QuotesListPage;
